import React, { useState, useEffect } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  Bar, XAxis, YAxis, CartesianGrid, Legend,
  ComposedChart, Line,
} from 'recharts';

/* ─── DATA ─────────────────────────────────────────────────── */
const KPI = [
  { label: 'AMBR Units Sold',   value: 26,   sub: 'since launch',          color: '#2563eb', bg: '#eff6ff' },
  { label: 'Standalone',        value: 2,    sub: '7.7% of orders',         color: '#16a34a', bg: '#f0fdf4' },
  { label: 'Bundled',           value: 24,   sub: '92.3% of orders',        color: '#f59e0b', bg: '#fffbeb' },
  { label: 'Countries Reached', value: 15,   sub: 'in first 5 days',        color: '#7c3aed', bg: '#f5f3ff' },
];

const DONUT = [
  { name: 'Bundled',    value: 24, color: '#2563eb' },
  { name: 'Standalone', value: 2,  color: '#16a34a' },
];

const COMPANIONS = [
  { name: 'CFA Level I Package',           orders: 12 },
  { name: 'Level I PDF Files & eBooks',    orders: 8  },
  { name: 'Sustainable Investing Cert',    orders: 3  },
  { name: 'Associate Society Membership',  orders: 3  },
  { name: 'Private Equity Certificate',    orders: 2  },
];

const DAILY = [
  { day: 'May 25', standalone: 0, bundled: 0 },
  { day: 'May 26', standalone: 0, bundled: 3 },
  { day: 'May 27', standalone: 1, bundled: 7 },
  { day: 'May 28', standalone: 1, bundled: 7 },
  { day: 'May 29', standalone: 0, bundled: 7 },
];

const COUNTRIES = [
  { flag: '🇺🇸', name: 'United States',  iso: 'US', count: 7,  standalone: 2, bundled: 5 },
  { flag: '🇬🇧', name: 'United Kingdom', iso: 'GB', count: 2,  standalone: 0, bundled: 2 },
  { flag: '🇰🇪', name: 'Kenya',          iso: 'KE', count: 2,  standalone: 0, bundled: 2 },
  { flag: '🇮🇩', name: 'Indonesia',      iso: 'ID', count: 2,  standalone: 0, bundled: 2 },
  { flag: '🌍', name: '11 other countries', iso: '', count: 13, standalone: 0, bundled: 13 },
];

/* ─── ANIMATED COUNTER ──────────────────────────────────────── */
function Counter({ target, duration = 1200 }: { target: number; duration?: number }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start = Math.min(start + step, target);
      setVal(start);
      if (start >= target) { clearInterval(timer); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <>{val}</>;
}

/* ─── CUSTOM DONUT LABEL ───────────────────────────────────── */
function DonutCenter({ cx, cy }: { cx?: number; cy?: number }) {
  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
      <tspan x={cx} dy="-0.4em" fontSize="2rem" fontWeight="700" fill="#0f172a">26</tspan>
      <tspan x={cx} dy="1.6em" fontSize="0.72rem" fill="#94a3b8" fontWeight="500" letterSpacing="0.06em">TOTAL UNITS</tspan>
    </text>
  );
}

/* ─── TOOLTIP ───────────────────────────────────────────────── */
const ChartTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) { return null; }
  return (
    <div style={{
      background: '#0f172a', color: '#f8fafc', padding: '0.6rem 0.9rem',
      borderRadius: 8, fontSize: '0.8rem', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
    }}>
      {label && <div style={{ marginBottom: '0.3rem', opacity: 0.6, fontWeight: 600 }}>{label}</div>}
      {payload.map((p: any) => (
        <div key={p.name} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || p.fill, flexShrink: 0 }} />
          <span style={{ opacity: 0.8 }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ─── SECTION HEADER ────────────────────────────────────────── */
function SectionHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>{title}</h3>
      {sub && <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>{sub}</p>}
    </div>
  );
}

/* ─── MAIN COMPONENT ────────────────────────────────────────── */
export function AMBRMockup() {
  const maxCompanion = Math.max(...COMPANIONS.map(c => c.orders));
  const maxCountry   = Math.max(...COUNTRIES.map(c => c.count));

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
      background: '#f8fafc',
      minHeight: '100vh',
      padding: '2rem',
    }}>

      {/* ── HEADER ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.75rem', flexWrap: 'wrap', gap: '1rem',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.3rem' }}>
            <h1 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em' }}>
              AMBR Purchase Intelligence
            </h1>
            <span style={{
              background: 'linear-gradient(135deg, #16a34a, #15803d)',
              color: '#fff', fontSize: '0.7rem', fontWeight: 700,
              padding: '0.2rem 0.6rem', borderRadius: 999, letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}>LIVE · LAUNCH PERIOD</span>
          </div>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>
            Associate Membership (SKU: AMBR) · Standalone vs bundled purchase behaviour
          </p>
        </div>

        {/* Date badge */}
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '0.6rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Launch window</div>
          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', marginTop: '0.1rem' }}>May 25 – May 29, 2026</div>
          <div style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 600, marginTop: '0.1rem' }}>↑ Day 5 of launch</div>
        </div>
      </div>

      {/* ── KPI ROW ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.25rem' }}>
        {KPI.map((k, i) => (
          <div key={i} style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
            padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              background: k.color,
            }} />
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
              {k.label}
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: 800, color: k.color, lineHeight: 1, marginBottom: '0.35rem', letterSpacing: '-0.03em' }}>
              <Counter target={k.value} />
            </div>
            <div style={{ display: 'inline-block', background: k.bg, color: k.color, fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999 }}>
              {k.sub}
            </div>
          </div>
        ))}
      </div>

      {/* ── MIDDLE ROW ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '1rem', marginBottom: '1.25rem' }}>

        {/* Donut */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <SectionHeader title="Purchase Mix" sub="Standalone vs bundled split" />
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={DONUT} cx="50%" cy="50%" innerRadius={68} outerRadius={95}
                paddingAngle={3} dataKey="value" strokeWidth={0}>
                {DONUT.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <DonutCenter cx={undefined} cy={undefined} />
              <Tooltip content={<ChartTip />} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '0.5rem' }}>
            {DONUT.map(d => (
              <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color }} />
                <span style={{ color: '#475569', fontWeight: 500 }}>{d.name}</span>
                <span style={{ color: d.color, fontWeight: 700 }}>{((d.value / 26) * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bundle companions */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <SectionHeader title="Top Bundle Companions" sub="Products most often purchased alongside AMBR" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginTop: '0.5rem' }}>
            {COMPANIONS.map((c, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.82rem' }}>
                  <span style={{ color: '#334155', fontWeight: 500, maxWidth: '75%' }}>{c.name}</span>
                  <span style={{ color: '#2563eb', fontWeight: 700 }}>{c.orders} orders</span>
                </div>
                <div style={{ height: 6, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 999,
                    width: `${(c.orders / maxCompanion) * 100}%`,
                    background: i === 0
                      ? 'linear-gradient(90deg, #2563eb, #3b82f6)'
                      : i === 1
                      ? 'linear-gradient(90deg, #0ea5e9, #38bdf8)'
                      : 'linear-gradient(90deg, #94a3b8, #cbd5e1)',
                    transition: 'width 0.8s cubic-bezier(0.34,1.56,0.64,1)',
                  }} />
                </div>
              </div>
            ))}
          </div>
          <div style={{
            marginTop: '1rem', padding: '0.75rem', background: '#eff6ff',
            borderRadius: 8, fontSize: '0.78rem', color: '#2563eb', fontWeight: 500,
          }}>
            💡 50% of bundled buyers include the full CFA Level I package — strong signal that AMBR appeals to new CFA candidates
          </div>
        </div>
      </div>

      {/* ── BOTTOM ROW ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '1rem' }}>

        {/* Daily trend */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <SectionHeader title="Daily Acquisition Trend" sub="Standalone vs bundled by day since launch" />
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={DAILY} barSize={28} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTip />} cursor={{ fill: '#f8fafc' }} />
              <Legend wrapperStyle={{ fontSize: '0.78rem', paddingTop: '0.5rem' }} />
              <Bar dataKey="bundled" name="Bundled" stackId="a" fill="#2563eb" radius={[0, 0, 0, 0]} />
              <Bar dataKey="standalone" name="Standalone" stackId="a" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="bundled" stroke="#2563eb" strokeWidth={0} dot={false} legendType="none" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Country table */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <SectionHeader title="Geographic Reach" sub="Orders by country · 15 countries in 5 days" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
            {COUNTRIES.map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.1rem', width: 24, textAlign: 'center' }}>{c.flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                    <span style={{ fontSize: '0.82rem', color: '#334155', fontWeight: 500 }}>{c.name}</span>
                    <span style={{ fontSize: '0.82rem', color: '#0f172a', fontWeight: 700 }}>{c.count}</span>
                  </div>
                  <div style={{ height: 5, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden', display: 'flex', gap: 1 }}>
                    <div style={{
                      height: '100%',
                      width: `${(c.bundled / maxCountry) * 100}%`,
                      background: i === 4 ? '#cbd5e1' : '#2563eb',
                      borderRadius: 999,
                    }} />
                    {c.standalone > 0 && (
                      <div style={{
                        height: '100%',
                        width: `${(c.standalone / maxCountry) * 100}%`,
                        background: '#16a34a',
                        borderRadius: 999,
                      }} />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem', display: 'flex', gap: '1rem', fontSize: '0.72rem', color: '#94a3b8' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: '#2563eb', display: 'inline-block' }} /> Bundled
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: '#16a34a', display: 'inline-block' }} /> Standalone
            </span>
          </div>
        </div>
      </div>

      {/* ── FOOTER NOTE ── */}
      <div style={{ marginTop: '1.25rem', fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center' }}>
        Data sourced from BigCommerce Prod · Orders with SKU AMBR · Launch date May 25, 2026 · Mockup — not yet connected to live warehouse
      </div>

    </div>
  );
}

export default AMBRMockup;
